import React from 'react';

import isEmpty from 'lodash/isEmpty';

import {
  TableFooter,
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  styled,
} from '@gogame/components';
import { useNumberFormat } from '@gogame/intl-ts';
import { ScreenLink } from '@gogame/screens';

import { usePeriodForScreenTracker } from 'scenes/PromoCodes/hooks';
import {
  FILTER_REGISTRATION_TYPE,
  FILTER_AFFILIATES,
  FILTER_PERIOD,
} from 'scenes/Trackers/Tracker/constants';
import { trackerLink } from 'scenes/Trackers/Tracker/navigation';

const EXCLUDE_BOTS = 1;
const BoldCell = styled(TableCell)`
  font-weight: bold;
`;
const getCellValue = (arr = [], type) =>
  arr.reduce((p, n) => p + (n[type] || 0), 0);

const RedeemTable = ({ data }) => {
  const periodForScreenTracker = usePeriodForScreenTracker();
  const numberFormatter = useNumberFormat();

  return (
    <TableContainer>
      <Table width={'100%'}>
        <TableHead>
          <TableRow>
            <TableCell col={'type'} width={'25%'}>
              Type
            </TableCell>
            <TableCell col={'promoCode'} width={'25%'} align={'right'}>
              <Tooltip
                wrapper
                inlineWrapper
                title={
                  'Number of users who redeemed the promo code and were attributed by it'
                }
              >
                Promo Code
              </Tooltip>
            </TableCell>
            <TableCell col={'mmp'} width={'25%'} align={'right'}>
              <Tooltip
                wrapper
                inlineWrapper
                title={
                  'Number of users who redeemed the promo code, but were previously attributed by the MMP'
                }
              >
                MMP
              </Tooltip>
            </TableCell>
            <BoldCell col={'total'} width={'25%'} align={'right'}>
              Total
            </BoldCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.redemptionBreakdown?.breakdown.map((item, i) => (
            <TableRow key={i}>
              <TableCell col={'type'} width={'25%'} intensity={400}>
                {item.type || '-'}
              </TableCell>
              <TableCell col={'promoCode'} align={'right'} width={'25%'}>
                {item.promoCode ? (
                  <React.Fragment>
                    <ScreenLink
                      target={'_blank'}
                      link={trackerLink}
                      params={{
                        ...(!isEmpty(data.goGameCampaigns) && {
                          dimensionSearchQuery: data.goGameCampaigns
                            .map((campaign) => campaign.name)
                            .join('|'),
                        }),
                        filtersCollection: [
                          {
                            [FILTER_REGISTRATION_TYPE]: EXCLUDE_BOTS,
                            ...(data.affiliateId && {
                              [FILTER_AFFILIATES]: [data.affiliateId],
                            }),
                            [FILTER_PERIOD]: periodForScreenTracker({
                              startDate: data.activityPeriod[0],
                            }),
                          },
                        ],
                        drillPath: [{ type: 'Campaign' }],
                        tableSettings: {
                          orderBy: 'RegActions',
                        },
                        ready: true,
                        columns: {
                          order: [
                            'RegActions',
                            'PromoCodeAttributedRedeemers',
                            'TotalDepositAmount',
                            'TotalArpu',
                            'TotalArppu',
                          ],
                        },
                        charts: [
                          {
                            metrics: ['PromoCodeAttributedRedeemers'],
                          },
                        ],
                      }}
                    >
                      {numberFormatter(item.promoCodeWithoutBots)}
                    </ScreenLink>
                    ({numberFormatter(item.promoCode)} with bots)
                  </React.Fragment>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell col={'mmp'} align={'right'} width={'25%'}>
                {item.mmp ? numberFormatter(item.mmp) : '-'}
              </TableCell>
              <BoldCell
                col={'total'}
                align={'right'}
                width={'25%'}
                intensity={200}
              >
                {item.total ? numberFormatter(item.total) : '-'}
              </BoldCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter width={'100%'}>
          <TableRow>
            <BoldCell>Total</BoldCell>
            <BoldCell align={'right'}>
              {numberFormatter(
                getCellValue(data?.redemptionBreakdown?.breakdown, 'promoCode')
              )}
            </BoldCell>
            <BoldCell align={'right'}>
              {numberFormatter(
                getCellValue(data?.redemptionBreakdown?.breakdown, 'mmp')
              )}
            </BoldCell>
            <BoldCell align={'right'}>
              {numberFormatter(data?.redemptionBreakdown?.total)}
            </BoldCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default RedeemTable;
